import {
  calculateEarliestAffectedCapitalizationMonth,
  type CalculateEarliestAffectedCapitalizationMonthResult,
  dateToMonthYear,
  getFirstDateOfMonthYear,
  getMonthLongAbbreviation,
  isMonthYearAfterOrEqual,
  isMonthYearBeforeOrEqual,
  MonthYear,
  stringToMonthYear,
} from '@common/neo.tax';

import { type AscProject } from '@neo.tax/asc/hooks';

import { AscProjectInfoForm } from './asc-project-detail-form.types';

export const makeDefaultFormValues = (ascProject: AscProject): AscProjectInfoForm => ({
  name: ascProject.name,
  description: ascProject.description ?? '',
  startMonthYear: ascProject.startDate && dateToMonthYear(ascProject.startDate),
  endMonthYear: ascProject.endDate && dateToMonthYear(ascProject.endDate),
  productId: ascProject.product?.id ?? null,
  projectOwnerId: ascProject.projectOwner?.id ?? null,
});

export const validateCapitalizationStartMonthYear =
  (ascProject: AscProject, capitalizationEndMonthYear: MonthYear | null) =>
  (value: MonthYear | null): string | undefined => {
    if (value === null && capitalizationEndMonthYear !== null) {
      return 'Required when capitalization end date is set';
    }
    if (value === null || ascProject.startDate === null || ascProject.endDate === null) {
      return;
    }

    const capitalizationStartDate = getFirstDateOfMonthYear(value);
    if (capitalizationStartDate < ascProject.startDate) {
      return `Must be on or after the project start date (${getMonthLongAbbreviation(
        ascProject.startDate.getUTCMonth(),
      )} ${ascProject.startDate.getUTCFullYear()})`;
    }
    if (capitalizationStartDate > ascProject.endDate) {
      return `Must be on or before the project end date (${getMonthLongAbbreviation(
        ascProject.endDate.getUTCMonth(),
      )} ${ascProject.endDate.getUTCFullYear()})`;
    }

    if (capitalizationEndMonthYear === null) {
      return;
    }
    if (!isMonthYearBeforeOrEqual(value, capitalizationEndMonthYear)) {
      return 'Must be on or before the capitalization end date';
    }
  };

export const validateCapitalizationEndMonthYear =
  (ascProject: AscProject, capitalizationStartMonthYear: MonthYear | null) =>
  (value: MonthYear | null): string | undefined => {
    if (value === null || ascProject.startDate === null || ascProject.endDate === null) {
      return;
    }

    const capitalizationEndDate = getFirstDateOfMonthYear(value);
    if (capitalizationEndDate < ascProject.startDate) {
      return `Must be on or after the project start date (${getMonthLongAbbreviation(
        ascProject.startDate.getUTCMonth(),
      )} ${ascProject.startDate.getUTCFullYear()})`;
    }
    if (capitalizationEndDate > ascProject.endDate) {
      return `Must be on or before the project end date (${getMonthLongAbbreviation(
        ascProject.endDate.getUTCMonth(),
      )} ${ascProject.endDate.getUTCFullYear()})`;
    }

    if (capitalizationStartMonthYear === null) {
      return;
    }
    if (!isMonthYearAfterOrEqual(value, capitalizationStartMonthYear)) {
      return 'Must be on or after the capitalization start date';
    }
  };

/**
 * Calculates the earliest month that would be affected by a capitalization period change.
 * Returns null if no approved months would be affected.
 *
 * @param oldCapitalizationStart - Original capitalization start month-year
 * @param oldCapitalizationEnd - Original capitalization end month-year
 * @param newCapitalizationStart - New capitalization start month-year
 * @param newCapitalizationEnd - New capitalization end month-year
 * @param approvedThroughMonthYear - The latest approved month in "YYYY-MM" format
 * @returns The earliest affected month-year with reason, or null if no approvals would be affected
 */
export const calculateEarliestAffectedMonth = (
  oldCapitalizationStart: MonthYear | null,
  oldCapitalizationEnd: MonthYear | null,
  newCapitalizationStart: MonthYear | null,
  newCapitalizationEnd: MonthYear | null,
  approvedThroughMonthYear: string | null,
): CalculateEarliestAffectedCapitalizationMonthResult => {
  if (!approvedThroughMonthYear) {
    return null;
  }

  return calculateEarliestAffectedCapitalizationMonth({
    oldCapitalizationStart,
    oldCapitalizationEnd,
    newCapitalizationStart,
    newCapitalizationEnd,
    approvedThrough: stringToMonthYear(approvedThroughMonthYear),
  });
};
